import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { TrendingUp, Calendar, Package, Search, Plus, X, Clock, CheckCircle, XCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const DealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userTheme } = useTheme();
  const { user } = useAuth();

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const dealsBox = userTheme?.dealsBox || {};
  const buttonStyles = userTheme?.buttons || {};

  // Fallback colors
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const headerBgColor = dealsBox.headerBackgroundColor || '#2B4B6A';
  const priceColor = dealsBox.priceColor || '#F58A5A';

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterOrder, setFilterOrder] = useState('expiration');

  // Seller submit form
  const isSeller = user?.account_type === 'seller' || user?.account_type === 'buyer_seller';
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMyDeals, setShowMyDeals] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    product: '',
    price: '',
    description: '',
    start_date: '',
    expiration_date: '',
    category_id: '',
    post_globally: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        api.get('/deals'),
        api.get('/deal-categories')
      ];
      if (isSeller) {
        promises.push(api.get('/seller/deals'));
      }
      const results = await Promise.all(promises);
      setDeals(results[0].data);
      setCategories(results[1].data);
      if (isSeller && results[2]) {
        setMyDeals(results[2].data);
      }
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const isExpired = (date) => new Date(date) < new Date();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.product || !formData.price || !formData.expiration_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/seller/deals', formData);
      toast.success('Opportunity submitted for admin approval');
      setShowSubmitForm(false);
      setFormData({ title: '', product: '', price: '', description: '', start_date: '', expiration_date: '', category_id: '', post_globally: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit opportunity');
    } finally {
      setSubmitting(false);
    }
  };

  // Apply filters
  const filteredDeals = deals
    .filter(deal => {
      if (filterCategory && deal.category_id !== parseInt(filterCategory)) return false;
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        if (!deal.title.toLowerCase().includes(search) && !deal.product.toLowerCase().includes(search)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (filterOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (filterOrder === 'price') return a.price.localeCompare(b.price);
      if (filterOrder === 'expiration') return new Date(a.expiration_date) - new Date(b.expiration_date);
      return 0;
    });

  const getStatusBadge = (deal) => {
    if (deal.approval_status === 'pending') return { label: 'Pending Approval', color: '#F59E0B', icon: Clock };
    if (deal.approval_status === 'rejected') return { label: 'Rejected', color: '#EF4444', icon: XCircle };
    if (deal.is_active) return { label: 'Active', color: '#10B981', icon: CheckCircle };
    return { label: 'Inactive', color: '#6B7280', icon: Clock };
  };

  if (loading) {
    return (
      <Sidebar>
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: primaryColor }}
              >
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1
                  className="text-2xl font-semibold"
                  style={{ color: pageStyles.titleColor || '#111827' }}
                >
                  Opportunities
                </h1>
                <p style={{ color: pageStyles.textColor || '#6B7280' }} className="text-sm">
                  Browse available trading opportunities
                </p>
              </div>
            </div>
            {isSeller && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setShowMyDeals(!showMyDeals); }}
                  className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
                  style={{
                    borderColor: primaryColor,
                    color: showMyDeals ? '#FFFFFF' : primaryColor,
                    backgroundColor: showMyDeals ? primaryColor : 'transparent'
                  }}
                >
                  My Submissions ({myDeals.length})
                </button>
                <button
                  onClick={() => setShowSubmitForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Plus className="w-4 h-4" />
                  Submit Opportunity
                </button>
              </div>
            )}
          </div>
        </div>

        {/* My Submissions (Seller) */}
        {isSeller && showMyDeals && myDeals.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: pageStyles.titleColor || '#111827' }}>
              My Submitted Opportunities
            </h3>
            <div className="grid gap-3">
              {myDeals.map((deal) => {
                const status = getStatusBadge(deal);
                const StatusIcon = status.icon;
                return (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between p-4 rounded-xl"
                    style={{
                      backgroundColor: dealsBox.backgroundColor || '#FFFFFF',
                      border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`
                    }}
                  >
                    <div>
                      <p className="font-medium" style={{ color: dealsBox.titleColor || '#111827' }}>{deal.title}</p>
                      <p className="text-sm" style={{ color: dealsBox.textColor || '#6B7280' }}>
                        {deal.product} &middot; {deal.price}
                      </p>
                    </div>
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${status.color}15`, color: status.color }}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div
          className="flex flex-wrap gap-4 mb-6 p-4 rounded-xl"
          style={{
            backgroundColor: dealsBox.backgroundColor || '#FFFFFF',
            border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`
          }}
        >
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:ring-2"
            style={{
              border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`,
              color: dealsBox.textColor || '#374151'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterOrder}
            onChange={(e) => setFilterOrder(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:ring-2"
            style={{
              border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`,
              color: dealsBox.textColor || '#374151'
            }}
          >
            <option value="expiration">Sort by Expiration</option>
            <option value="newest">Sort by Newest</option>
            <option value="price">Sort by Price</option>
          </select>

          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: dealsBox.textColor || '#9CA3AF' }} />
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search opportunities..."
                className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:ring-2"
                style={{
                  border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`,
                  color: dealsBox.textColor || '#374151'
                }}
              />
            </div>
          </div>
        </div>

        {/* Opportunities List */}
        {filteredDeals.length === 0 ? (
          <div
            className="rounded-xl p-12 text-center"
            style={{
              backgroundColor: dealsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${dealsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <TrendingUp className="w-16 h-16 mx-auto mb-4" style={{ color: dealsBox.borderColor || '#D1D5DB' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: dealsBox.titleColor || '#4B5563' }}>
              No opportunities found
            </h3>
            <p style={{ color: dealsBox.textColor || '#6B7280' }}>
              Try adjusting your filters or check back soon.
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredDeals.map((deal) => (
              <div
                key={deal.id}
                className={`rounded-xl shadow-sm overflow-hidden ${isExpired(deal.expiration_date) ? 'opacity-60' : ''}`}
                style={{
                  backgroundColor: dealsBox.backgroundColor || '#FFFFFF',
                  border: `1px solid ${isExpired(deal.expiration_date) ? '#D1D5DB' : dealsBox.borderColor || '#E5E7EB'}`
                }}
              >
                {/* Card Header */}
                <div
                  className="px-6 py-4"
                  style={{
                    backgroundColor: isExpired(deal.expiration_date) ? '#F3F4F6' : headerBgColor
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className={`text-xl font-bold ${isExpired(deal.expiration_date) ? '' : 'text-white'}`}
                        style={{ color: isExpired(deal.expiration_date) ? dealsBox.textColor || '#6B7280' : '#FFFFFF' }}
                      >
                        {deal.title}
                      </h2>
                      {deal.category_name && (
                        <span
                          className="inline-block mt-1 px-2 py-0.5 rounded text-xs"
                          style={{
                            backgroundColor: isExpired(deal.expiration_date) ? '#E5E7EB' : 'rgba(255,255,255,0.2)',
                            color: isExpired(deal.expiration_date) ? dealsBox.textColor || '#6B7280' : '#FFFFFF'
                          }}
                        >
                          {deal.category_name}
                        </span>
                      )}
                    </div>
                    {isExpired(deal.expiration_date) && (
                      <span
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}
                      >
                        Expired
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5" style={{ color: dealsBox.textColor || '#9CA3AF' }} />
                      <div>
                        <p className="text-xs" style={{ color: dealsBox.textColor || '#6B7280' }}>Product</p>
                        <p className="font-medium" style={{ color: dealsBox.titleColor || '#111827' }}>{deal.product}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-xs" style={{ color: dealsBox.textColor || '#6B7280' }}>Price</p>
                        <p className="font-medium text-lg" style={{ color: priceColor }}>{deal.price}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5" style={{ color: dealsBox.textColor || '#9CA3AF' }} />
                      <div>
                        <p className="text-xs" style={{ color: dealsBox.textColor || '#6B7280' }}>Valid Until</p>
                        <p className="font-medium" style={{ color: dealsBox.titleColor || '#111827' }}>
                          {new Date(deal.expiration_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {deal.description && (
                    <p className="mt-4 whitespace-pre-line" style={{ color: dealsBox.textColor || '#6B7280' }}>{deal.description}</p>
                  )}

                  {!isExpired(deal.expiration_date) && (
                    <div className="mt-6 pt-4" style={{ borderTop: `1px solid ${dealsBox.borderColor || '#E5E7EB'}` }}>
                      <Link
                        to="/admin/requests/new"
                        className="inline-flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all hover:opacity-90"
                        style={{
                          backgroundColor: primaryColor,
                          color: buttonStyles.primaryTextColor || '#FFFFFF'
                        }}
                      >
                        Submit LOI for this Opportunity
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Opportunity Modal */}
      {showSubmitForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Submit Opportunity</h2>
              <button onClick={() => setShowSubmitForm(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. ICUMSA 45 - Miami Stock"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <input
                  type="text"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. ICUMSA 45"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. $968 per MT"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your opportunity..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date *</label>
                  <input
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Your opportunity will be reviewed by an admin before it becomes visible to other users.
                </p>
              </div>
              {/* Post Globally Option */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.post_globally}
                    onChange={(e) => setFormData({ ...formData, post_globally: e.target.checked })}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-blue-900">Post Opportunity Globally</span>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Your offer will appear on the Trados network and be visible to all companies across the platform.
                    </p>
                  </div>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSubmitForm(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default DealsPage;
